import React, { useRef } from "react";
import {
  EmptyState,
  IconButton,
  Table,
  TableHeader,
  TableProps,
  TableRowItem,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { useTranslation } from "react-i18next";
import { SquarePen, Trash2 } from "lucide-react";
import { useAllowedDomainsStore } from "../../stores/Hooks";
import { ALLOWED_DOMAINS_PER_PAGE } from "../../stores/AllowedDomainsStore";
import { StyledEllipsisTypography } from "../../../../components/StyledEllipsisTypography";
import { Instance } from "mobx-state-tree";
import { Stack } from "@mui/material";
import { observer } from "mobx-react";
import { EMPTY_CHARACTER } from "@pulse/shared-components";
import { AllowedDomainModel } from "../../models/AllowedDomainModel";
import { SearchX } from "lucide-react";

const DOMAIN_NAME_COLUMN_WIDTH = "420px";
const DOMAIN_COLUMN_WIDTH = "420px";
const ACTION_COLUMN_WIDTH = "128px";
const TABLE_WIDTH = `calc(${DOMAIN_NAME_COLUMN_WIDTH} + ${DOMAIN_COLUMN_WIDTH} + ${ACTION_COLUMN_WIDTH})`;

enum AllowedDomainsTableColumn {
  DomainName = "DOMAIN_NAME",
  Domain = "DOMAIN",
  Actions = "ACTIONS",
}

interface AllowedDomainsTableProps {
  onEditAllowedDomain: (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ) => void;
  onDeleteAllowedDomain: (
    allowedDomain: Instance<typeof AllowedDomainModel>,
  ) => void;
}

export const AllowedDomainsTable: React.FC<AllowedDomainsTableProps> = observer(
  ({ onEditAllowedDomain, onDeleteAllowedDomain }) => {
    const { t } = useTranslation();
    const translate = t as unknown as (
      key: string,
      options?: Record<string, unknown>,
    ) => string;
    const spacing = useSpacing();
    const typography = useTypography();
    const allowedDomainsStore = useAllowedDomainsStore();
    const isInitialLoad = useRef(true);

    const allowedDomainsTableHeaders: TableHeader = [
      {
        columnId: AllowedDomainsTableColumn.DomainName,
        label: translate("emailCycles.allowedDomainsDomainNameColumnLabel"),
        width: DOMAIN_NAME_COLUMN_WIDTH,
      },
      {
        columnId: AllowedDomainsTableColumn.Domain,
        label: translate("emailCycles.allowedDomainsDomainColumnLabel"),
        width: DOMAIN_COLUMN_WIDTH,
      },
      {
        columnId: AllowedDomainsTableColumn.Actions,
        label: EMPTY_CHARACTER,
        width: ACTION_COLUMN_WIDTH,
      },
    ];

    const paginationOption: TableProps["paginationOption"] = {
      itemsPerPage: ALLOWED_DOMAINS_PER_PAGE,
      getPageIndicatorText: (startItem, endItem, totalItems): string =>
        t("common.paginationIndicationText", {
          startItem,
          endItem,
          totalItems,
        }),
    };

    const getAllowedDomainTableCells = (
      allowedDomain: Instance<typeof AllowedDomainModel>,
    ): TableRowItem["cells"] => [
      {
        columnId: AllowedDomainsTableColumn.DomainName,
        data: (
          <StyledEllipsisTypography
            data={allowedDomain.domainName.name}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: AllowedDomainsTableColumn.Domain,
        data: (
          <StyledEllipsisTypography
            data={allowedDomain.domain.name}
            typography={typography}
            t={t}
          />
        ),
      },
      {
        columnId: AllowedDomainsTableColumn.Actions,
        data: (
          <Stack direction="row" justifyContent="end" width="100%">
            <div onClick={(event) => event.stopPropagation()}>
              <IconButton
                name="editAllowedDomainButton"
                size="small"
                icon={<SquarePen />}
                variant="plain-neutral"
                onClick={() => onEditAllowedDomain(allowedDomain)}
                hoverColor="buttonOnSurfaceIcon"
              />
            </div>
            <div onClick={(event) => event.stopPropagation()}>
              <IconButton
                name="deleteAllowedDomainButton"
                size="small"
                icon={<Trash2 />}
                variant="plain-color"
                onClick={() => onDeleteAllowedDomain(allowedDomain)}
                color="destructive"
              />
            </div>
          </Stack>
        ),
      },
    ];

    const onTableOptionsChange: TableProps["onTableOptionsChange"] = async (
      options,
      setTotalItems,
    ) => {
      const pageIndex = options.pageNumber ?? 1;

      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      } else {
        await allowedDomainsStore.getAllowedDomains(pageIndex, true);
      }

      setTotalItems(allowedDomainsStore.totalItems);
      return allowedDomainsStore.allowedDomains.map((allowedDomain) => ({
        id: allowedDomain.id,
        cells: getAllowedDomainTableCells(allowedDomain),
      }));
    };

    const tableKey = `${allowedDomainsStore.currentPageIndex}-${allowedDomainsStore.totalItems}-${allowedDomainsStore.allowedDomains
      .map((allowedDomain) => allowedDomain.id)
      .join(",")}`;

    return (
      <Stack gap={spacing.spaceSM} width="100%" maxWidth={TABLE_WIDTH}>
        <Table
          key={tableKey}
          name="allowedDomainsTable"
          header={allowedDomainsTableHeaders}
          paginationOption={paginationOption}
          onTableOptionsChange={onTableOptionsChange}
          viewOverrides={{
            empty: {
              icon: (
                <EmptyState
                  icon={<SearchX />}
                  title={t("emailTemplates.emptyTemplatesMessage")}
                  description={EMPTY_CHARACTER}
                  width="100%"
                />
              ),
              message: EMPTY_CHARACTER,
            },
            loading: {
              message: t("common.searchTableLoadingState"),
            },
          }}
          onRowClick={(row) => {
            const allowedDomain = allowedDomainsStore.allowedDomains.find(
              (domain) => domain.id === row.id,
            );
            if (allowedDomain) {
              onEditAllowedDomain(allowedDomain);
            }
          }}
          leftPinnedColumnIds={[]}
          rightPinnedColumnIds={[AllowedDomainsTableColumn.Actions]}
        />
      </Stack>
    );
  },
);
